import React, { useState, useCallback, useEffect } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { FaTrash, FaCheck, FaShip, FaCopy, FaArrowLeft } from 'react-icons/fa';
import { BiRotateRight } from 'react-icons/bi';
import { getDatabase, ref, get, update, onValue } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import { getGridSize, getShipConfiguration } from '../services/gameService';

// Desktop uses HTML5 backend
const DndBackend = HTML5Backend;

// Default ship configuration - will be overridden by room settings
const DEFAULT_SHIPS = [
  { id: 'carrier', name: 'Carrier', size: 5, color: 'bg-blue-500' },
  { id: 'battleship', name: 'Battleship', size: 4, color: 'bg-green-500' },
  { id: 'cruiser', name: 'Cruiser', size: 3, color: 'bg-yellow-500' },
  { id: 'destroyer', name: 'Destroyer', size: 2, color: 'bg-red-500' },
  { id: 'scout', name: 'Scout', size: 2, color: 'bg-violet-500' }
];

// Desktop Ship component - optimized for mouse interactions
const Ship = ({ ship, isPlaced, onClick, rotation = 0, position, disabled }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'ship',
    item: () => ({
      id: ship.id,
      ship: { ...ship },
      rotation,
      isPlaced,
      position,
    }),
    canDrag: !disabled,
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  }), [ship.id, rotation, isPlaced, disabled]);

  const handleClick = (e) => {
    e.stopPropagation();
    if (!disabled && onClick) onClick();
  };

  return (
    <div
      ref={drag}
      onClick={handleClick}
      className={`
        ship-draggable flex flex-row relative
        ${isPlaced ? 'cursor-move' : disabled ? 'cursor-not-allowed opacity-50' : 'cursor-move hover:scale-105'} 
        transition-all duration-300
        ${isDragging ? 'opacity-50' : 'opacity-100'}
        select-none
      `}
      style={{
        transform: `rotate(${rotation}deg)`,
        transformOrigin: 'center',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        userSelect: 'none',
        gap: '0px',
      }}
    >
      {Array(ship.size).fill(null).map((_, i) => (
        <div
          key={i}
          className={`
            w-10 h-10 md:w-12 md:h-12 ${ship.color}
            border border-slate-600 rounded
            transition-all duration-200
            select-none
          `}
        />
      ))}
      {!isPlaced && (
        <div className="absolute -top-8 left-0 text-white text-sm 
                      bg-slate-800/90 px-2 py-1 rounded-md flex items-center gap-2
                      whitespace-nowrap select-none">
          <FaShip />
          {ship.name}
        </div>
      )}
    </div>
  );
};

// Dock Ship Component (Desktop Mouse Optimized)
const DockShip = ({ ship, isPlaced, isSelected, onClick, disabled }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'ship',
    item: () => ({
      id: ship.id,
      ship: { ...ship },
      rotation: 0,
      isPlaced: false,
      position: null,
    }),
    canDrag: !isPlaced && !disabled,
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  }), [ship, isPlaced, disabled]);

  return (
    <div
      ref={drag}
      onClick={onClick}
      className={`
        relative flex flex-col items-center justify-center p-2 rounded-xl transition-all duration-300
        ${isPlaced
          ? 'opacity-30 grayscale cursor-default scale-90'
          : disabled
            ? 'cursor-not-allowed opacity-50 grayscale'
            : 'cursor-grab active:cursor-grabbing hover:scale-105 active:scale-95 shadow-lg bg-slate-700/50 border border-slate-600/50 backdrop-blur-sm'
        }
        ${isSelected && !isPlaced && !disabled ? 'ring-2 ring-blue-500 shadow-blue-500/20 shadow-xl scale-105 bg-slate-700' : ''}
        ${isDragging ? 'opacity-50' : 'opacity-100'}
      `}
      style={{ minWidth: '90px', height: '90px' }}
    >
      {/* Mini Ship Representation */}
      <div className="flex gap-0.5 pointer-events-none mb-2">
        {Array(ship.size).fill(null).map((_, i) => (
          <div key={i} className={`w-3 h-3 rounded-[1px] ${ship.color} shadow-sm`} />
        ))}
      </div>
      <span className="text-xs font-bold text-slate-400 uppercase tracking-widest max-w-[80px] truncate text-center">
        {ship.name}
      </span>
    </div>
  );
};

// PlacedShipCell Component - Draggable cell for placed ships (Desktop)
const PlacedShipCell = ({ ship, isSelected, isShipStart, shipRotation, disabled, onSelect }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'ship',
    item: () => ({
      id: ship.id,
      ship: { ...ship },
      rotation: shipRotation,
      isPlaced: true,
      position: null,
    }),
    canDrag: !disabled && isShipStart, // Only first cell is draggable
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  }), [ship, isShipStart, shipRotation, disabled]);

  return (
    <div
      ref={isShipStart ? drag : null}
      className={`w-full h-full ${ship.color} rounded border-2 ${isSelected ? 'border-white' : 'border-gray-600'
        } ${isShipStart && !disabled ? 'cursor-move' : 'cursor-pointer'} transition-all ${isDragging ? 'opacity-50' : 'opacity-100'
        }`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
    />
  );
};

// Desktop Cell component - precise mouse targets
const Cell = ({ x, y, onDrop, onHover, onClick, isValidPlacement, children, disabled, gridSize = 8 }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'ship',
    drop: (item) => {
      if (!disabled) {
        onDrop(x, y, item.ship, item.rotation === 90 || item.rotation === 270);
      }
      return undefined;
    },
    hover: (item) => {
      if (!disabled) {
        onHover(x, y, item.ship, item.rotation === 90 || item.rotation === 270);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver()
    })
  }), [x, y, onDrop, onHover, disabled]);

  const handleClick = () => {
    if (!disabled && onClick) onClick();
  };

  const colLabel = String.fromCharCode(65 + x); // A-H
  const rowLabel = gridSize - y; // 8 down to 1
  const label = `${colLabel}${rowLabel}`;

  return (
    <div
      ref={drop}
      onClick={handleClick}
      className={`
        w-full h-full border border-slate-600 rounded
        transition-all duration-200 select-none relative
        flex items-center justify-center group
        ${isOver && isValidPlacement ? 'bg-green-500/50 border-green-400' : ''}
        ${isOver && !isValidPlacement ? 'bg-red-500/50 border-red-400' : ''}
        ${!isOver ? 'bg-slate-700 hover:bg-slate-600' : ''}
      `}
      title={`Cell ${label}`}
    >
      {children}
      {!children && (
        <span className="text-[20px] text-slate-500/50 group-hover:text-slate-400 font-mono">
          {label}
        </span>
      )}
    </div>
  );
};

const ShipPlacement_Desktop = () => {
  const navigate = useNavigate();

  // State for dynamic configuration
  const [GRID_SIZE, setGridSize] = useState(8);
  const [SHIPS, setShips] = useState(DEFAULT_SHIPS);
  const [MAX_SHIPS, setMaxShips] = useState(5);
  const [roomData, setRoomData] = useState(null);
  const [roomId, setRoomId] = useState('');
  const [_isHost, setIsHost] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Existing state
  const [grid, setGrid] = useState([]);
  const [placedShips, setPlacedShips] = useState(new Map());
  const [selectedShip, setSelectedShip] = useState(null);
  const [shipRotations, setShipRotations] = useState(new Map());
  const [hoverCoords, setHoverCoords] = useState(null);
  const [catalogQueue, setCatalogQueue] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  // Initialize grid based on size
  const createEmptyGrid = useCallback((size) => {
    return Array(size).fill().map(() => Array(size).fill(null));
  }, []);

  // Load room settings and configure game
  useEffect(() => {
    const loadRoomSettings = async () => {
      try {
        const currentRoomId = localStorage.getItem('battleshipRoomId');
        const currentPlayerId = localStorage.getItem('battleshipPlayerId');

        if (!currentRoomId) {
          setGrid(createEmptyGrid(8));
          setCatalogQueue([...DEFAULT_SHIPS]);
          return;
        }

        setRoomId(currentRoomId);

        const db = getDatabase();
        const roomRef = ref(db, `rooms/${currentRoomId}`);
        const snapshot = await get(roomRef);
        const roomData = snapshot.val();

        if (roomData) {
          setRoomData(roomData);

          if (currentPlayerId && roomData.hostId) {
            setIsHost(currentPlayerId === roomData.hostId);
          }

          console.log('Room data loaded:', {
            roomId: currentRoomId,
            gameMode: roomData.gameMode,
            hostId: roomData.hostId,
            currentPlayerId,
            isHost: currentPlayerId === roomData.hostId
          });

          if (roomData.settings) {
            const gridSize = getGridSize(roomData.settings);
            const ships = getShipConfiguration(roomData.settings);

            setGridSize(gridSize);
            setShips(ships);
            setMaxShips(ships.length);
            setGrid(createEmptyGrid(gridSize));
            setCatalogQueue([...ships]);
          } else {
            setGrid(createEmptyGrid(8));
            setCatalogQueue([...DEFAULT_SHIPS]);
          }
        } else {
          setGrid(createEmptyGrid(8));
          setCatalogQueue([...DEFAULT_SHIPS]);
        }
      } catch (error) {
        console.error('Error loading room settings:', error);
        setGrid(createEmptyGrid(8));
        setCatalogQueue([...DEFAULT_SHIPS]);
      }
    };

    loadRoomSettings();
  }, [createEmptyGrid]);

  const checkValidPlacement = useCallback((x, y, ship, vertical) => {
    if (!ship) return false;
    if (!grid || grid.length === 0) return false;

    const size = ship.size;

    // Check bounds
    if (vertical && y + size > GRID_SIZE) return false;
    if (!vertical && x + size > GRID_SIZE) return false;
    if (x < 0 || y < 0) return false;

    // Check if cells are occupied by other ships
    for (let i = 0; i < size; i++) {
      const checkX = vertical ? x : x + i;
      const checkY = vertical ? y + i : y;

      if (checkY >= GRID_SIZE || checkX >= GRID_SIZE) return false;

      const cellContent = grid[checkY] && grid[checkY][checkX];
      if (cellContent && cellContent !== ship.id) return false;
    }

    if (!placedShips.has(ship.id) && placedShips.size >= MAX_SHIPS) return false;

    return true;
  }, [grid, placedShips, GRID_SIZE, MAX_SHIPS]);

  const handleDrop = (x, y, ship, shipIsVertical) => {
    if (!checkValidPlacement(x, y, ship, shipIsVertical)) {
      return;
    }

    // Clear previous placement of this ship
    const newGrid = grid.map(row => row.map(cell => cell === ship.id ? null : cell));

    // Place ship in new position
    for (let i = 0; i < ship.size; i++) {
      const placeX = shipIsVertical ? x : x + i;
      const placeY = shipIsVertical ? y + i : y;
      if (placeY >= 0 && placeY < GRID_SIZE && placeX >= 0 && placeX < GRID_SIZE) {
        newGrid[placeY][placeX] = ship.id;
      }
    }

    setGrid(newGrid);

    const newPlacedShips = new Map(placedShips);
    newPlacedShips.set(ship.id, { x, y, vertical: shipIsVertical });
    setPlacedShips(newPlacedShips);

    const newRotations = new Map(shipRotations);
    newRotations.set(ship.id, shipIsVertical ? 90 : 0);
    setShipRotations(newRotations);

    if (!placedShips.has(ship.id)) {
      setCatalogQueue(current => current.filter(s => s.id !== ship.id));
    }

    setSelectedShip(ship);
  };

  const handleRotateShip = () => {
    if (!selectedShip) return;

    const shipPosition = placedShips.get(selectedShip.id);
    if (!shipPosition) return;

    const newVertical = !shipPosition.vertical;
    if (checkValidPlacement(shipPosition.x, shipPosition.y, selectedShip, newVertical)) {
      handleDrop(shipPosition.x, shipPosition.y, selectedShip, newVertical);
    }
  };

  const handleHover = (x, y, ship, shipIsVertical) => {
    setHoverCoords({ x, y, ship, isVertical: shipIsVertical });
  };

  const handleClearPlacement = () => {
    setGrid(Array(GRID_SIZE).fill().map(() => Array(GRID_SIZE).fill(null)));
    setPlacedShips(new Map());
    setShipRotations(new Map());
    setSelectedShip(null);
    setCatalogQueue([...SHIPS].slice(0, MAX_SHIPS));
  };

  const handleAutoPlace = () => {
    const newGrid = Array(GRID_SIZE).fill().map(() => Array(GRID_SIZE).fill(null));
    const newPlacedShips = new Map();
    const newRotations = new Map();

    SHIPS.forEach((ship) => {
      let placed = false;
      const maxAttempts = 100;
      let attempts = 0;

      while (!placed && attempts < maxAttempts) {
        const x = Math.floor(Math.random() * GRID_SIZE);
        const y = Math.floor(Math.random() * GRID_SIZE);
        const vertical = Math.random() < 0.5;

        const fitsHorizontally = !vertical && x + ship.size <= GRID_SIZE;
        const fitsVertically = vertical && y + ship.size <= GRID_SIZE;

        if ((fitsHorizontally || fitsVertically) && x >= 0 && y >= 0) {
          let canPlace = true;
          for (let i = 0; i < ship.size; i++) {
            const checkX = vertical ? x : x + i;
            const checkY = vertical ? y + i : y;
            if (newGrid[checkY][checkX]) {
              canPlace = false;
              break;
            }
          }

          if (canPlace) {
            for (let i = 0; i < ship.size; i++) {
              const placeX = vertical ? x : x + i;
              const placeY = vertical ? y + i : y;
              newGrid[placeY][placeX] = ship.id;
            }

            newPlacedShips.set(ship.id, { x, y, vertical });
            newRotations.set(ship.id, vertical ? 90 : 0);
            placed = true;
          }
        }
        attempts++;
      }
    });

    setGrid(newGrid);
    setPlacedShips(newPlacedShips);
    setShipRotations(newRotations);
    setCatalogQueue([]);
    setSelectedShip(null);
  };

  const updateShipPlacement = async (roomId, playerId, placementData) => {
    try {
      const db = getDatabase();
      const playerRef = ref(db, `rooms/${roomId}/players/${playerId}`);
      const playerSnapshot = await get(playerRef);

      if (!playerSnapshot.exists()) {
        console.error("Player not found in room");
        return false;
      }

      const updates = {};
      updates[`rooms/${roomId}/players/${playerId}/PlacementData`] = placementData;

      await update(ref(db), updates);
      console.log("Ship placement data updated successfully");
      return true;
    } catch (error) {
      console.error("Error updating ship placement:", error);
      return false;
    }
  };

  const handleComplete = async () => {
    if (placedShips.size === SHIPS.length) {
      const playerId = localStorage.getItem('battleshipPlayerId');
      const roomId = localStorage.getItem('battleshipRoomId');

      if (!playerId || !roomId) {
        setErrorMessage("You must be in a room to place ships");
        return;
      }

      setIsSaving(true);
      setErrorMessage('');

      try {
        const shipPlacements = {};
        placedShips.forEach((position, shipId) => {
          const ship = SHIPS.find(s => s.id === shipId);
          shipPlacements[shipId] = {
            name: ship.name,
            startPosition: { x: position.x, y: position.y },
            orientation: position.vertical ? 'vertical' : 'horizontal',
            size: ship.size
          };
        });

        const gridForDatabase = Array(GRID_SIZE).fill().map(() =>
          Array(GRID_SIZE).fill().map(() => ({
            ship: null,
            hit: false,
            miss: false
          }))
        );

        placedShips.forEach((position, shipId) => {
          const ship = SHIPS.find(s => s.id === shipId);
          for (let i = 0; i < ship.size; i++) {
            const x = position.vertical ? position.x : position.x + i;
            const y = position.vertical ? position.y + i : position.y;
            gridForDatabase[y][x].ship = shipId;
          }
        });

        const placementData = {
          grid: gridForDatabase,
          ships: shipPlacements,
          lastUpdated: Date.now()
        };

        const success = await updateShipPlacement(roomId, playerId, placementData);

        if (!success) {
          throw new Error('Failed to update ship placement');
        }

        setIsSaving(false);
        setIsSaved(true);

      } catch (error) {
        setErrorMessage(error.message);
        setIsSaving(false);
      }
    }
  };

  const handleReady = async () => {
    console.log("handleReady called, isSaved:", isSaved);

    if (isSaved) {
      try {
        const roomId = localStorage.getItem('battleshipRoomId');
        const playerId = localStorage.getItem('battleshipPlayerId');
        const db = getDatabase();

        await update(ref(db, `rooms/${roomId}/players/${playerId}`), {
          ready: true
        });

        const roomRef = ref(db, `rooms/${roomId}`);
        const snapshot = await get(roomRef);
        const room = snapshot.val();

        if (!room) {
          setErrorMessage("Room not found");
          return;
        }

        const gameMode = room.gameMode || 'admin';
        const players = Object.values(room.players || {});
        const allPlayersReady = players.length === 2 && players.every(player => player.ready);

        if (allPlayersReady) {
          if (gameMode === 'random' || gameMode === 'friendly' || gameMode === 'ai') {
            setStatusMessage("Both players ready! Starting game in 3 seconds...");

            setTimeout(() => {
              setStatusMessage("Game starting in 2...");
              setTimeout(() => {
                setStatusMessage("Game starting in 1...");
                setTimeout(async () => {
                  try {
                    if (gameMode === 'ai') {
                      const { startAiGame } = await import('../services/aiGameService');
                      await startAiGame(roomId);
                    } else {
                      const { startGame } = await import('../services/adminService');
                      await startGame(roomId);
                    }
                    setStatusMessage("Starting game...");
                  } catch (error) {
                    console.error("Error starting game:", error);
                    setStatusMessage("Error starting game. Please try again...");
                  }
                }, 1000);
              }, 1000);
            }, 1000);
          } else {
            setStatusMessage("Both players ready! Waiting for admin to start the game...");
          }
        } else {
          setStatusMessage("Waiting for other player to get ready...");
        }
      } catch (error) {
        console.error("Error in handleReady:", error);
        setErrorMessage(error.message);
      }
    } else {
      setErrorMessage("Please save your ship placement first");
    }
  };

  const handleDeleteShip = (shipId) => {
    const shipToAdd = SHIPS.find(s => s.id === shipId);

    const newGrid = grid.map(row =>
      row.map(cell => cell === shipId ? null : cell)
    );
    setGrid(newGrid);

    const newPlacedShips = new Map(placedShips);
    newPlacedShips.delete(shipId);
    setPlacedShips(newPlacedShips);

    if (shipToAdd && !catalogQueue.some(s => s.id === shipId)) {
      setCatalogQueue(current => [...current, shipToAdd]);
    }

    const newRotations = new Map(shipRotations);
    newRotations.delete(shipId);
    setShipRotations(newRotations);

    setSelectedShip(null);
  };

  const handleGridCellClick = (x, y) => {
    if (isSaved) return;

    const shipId = grid[y][x];

    if (shipId) {
      const ship = SHIPS.find(s => s.id === shipId);
      setSelectedShip(ship);
    } else if (selectedShip && !placedShips.has(selectedShip.id)) {
      const isVertical = (shipRotations.get(selectedShip.id) || 0) === 90;
      if (checkValidPlacement(x, y, selectedShip, isVertical)) {
        handleDrop(x, y, selectedShip, isVertical);
      }
    }
  };

  useEffect(() => {
    const roomId = localStorage.getItem('battleshipRoomId');
    const playerId = localStorage.getItem('battleshipPlayerId');

    if (!roomId || !playerId) return;

    const roomRef = ref(getDatabase(), `rooms/${roomId}`);

    const unsubscribe = onValue(roomRef, (snapshot) => {
      const room = snapshot.val();
      if (!room || !room.players) return;

      const players = Object.entries(room.players);
      if (players.length < 2) return;

      if (isSaved) {
        const otherPlayer = players.find(([id]) => id !== playerId);
        const gameMode = room.gameMode || 'admin';

        if (otherPlayer && !otherPlayer[1].ready) {
          setStatusMessage("Waiting for other player to get ready...");
        } else {
          // Check if it's an AI game or auto-start game mode
          if (gameMode === 'ai' || gameMode === 'random' || gameMode === 'friendly') {
            setStatusMessage("Both players ready! Starting game automatically...");
          } else {
            setStatusMessage("Both players ready! Waiting for admin to start the game...");
          }
        }
      }

      if (room.countdown) {
        setStatusMessage(`Game starting in ${room.countdown}...`);
      }

      if (room.gameStarted) {
        navigate(`/room/${roomId}`);
      }
    });

    return () => unsubscribe();
  }, [isSaved, navigate]);

  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  const handleCopyRoomId = async () => {
    console.log('Copy button clicked, roomId:', roomId);

    if (!roomId) {
      console.error('No room ID to copy');
      return;
    }

    try {
      await navigator.clipboard.writeText(roomId);
      console.log('Successfully copied to clipboard:', roomId);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.log('Clipboard API failed, using fallback:', error);
      try {
        const textArea = document.createElement('textarea');
        textArea.value = roomId;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (successful) {
          console.log('Fallback copy successful');
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
        } else {
          console.error('Fallback copy failed');
        }
      } catch (fallbackError) {
        console.error('Both clipboard methods failed:', fallbackError);
      }
    }
  };

  return (
    <DndProvider backend={DndBackend}>
      <div className="min-h-screen bg-slate-950 p-5 flex flex-col gap-5 select-none text-slate-200">

        {/* Main Workspace Row */}
        <div className="flex-1 flex gap-5 min-h-0 h-[calc(100vh-14rem)]">

          {/* Left Column - Code & Tools */}
          <div className="w-1/5 flex flex-col gap-5">

            {/* Room Code Card */}
            <div className="bg-slate-900/50 p-5 rounded-3xl border border-slate-700/50 shadow-xl backdrop-blur-sm">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4 text-center">
                Room Code
              </h3>
              {roomData && roomData.gameMode === 'friendly' && roomId && (
                <div className="text-center">
                  <div className="bg-slate-950/50 text-white px-4 py-3 rounded-xl font-mono text-xl font-bold mb-3 border border-slate-700/50">
                    {roomId}
                  </div>
                  <button
                    onClick={handleCopyRoomId}
                    className={`px-4 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 mx-auto text-sm ${copySuccess
                      ? 'bg-green-600/20 text-green-400'
                      : 'bg-blue-600/20 hover:bg-blue-600/30 text-blue-400'
                      }`}
                  >
                    {copySuccess ? <FaCheck size={14} /> : <FaCopy size={14} />}
                    {copySuccess ? 'Copied' : 'Copy'}
                  </button>
                </div>
              )}
              {(!roomData || roomData.gameMode !== 'friendly' || !roomId) && (
                <div className="text-center text-slate-500 py-2">
                  <p className="font-semibold">Matchmaking</p>
                  <p className="text-xs mt-1">Searching for opponent...</p>
                </div>
              )}
            </div>

            {/* Tools Card */}
            <div className="flex-1 bg-slate-900/50 p-5 rounded-3xl border border-slate-700/50 shadow-xl backdrop-blur-sm flex flex-col gap-3">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-2 text-center">
                Tools
              </h3>

              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={handleRotateShip}
                  disabled={!selectedShip || !placedShips.has(selectedShip?.id) || isSaved}
                  className={`p-2 rounded-xl font-medium flex flex-col items-center justify-center gap-1 transition-all duration-200 aspect-square ${selectedShip && placedShips.has(selectedShip.id) && !isSaved
                    ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
                    : 'bg-slate-800/50 text-slate-600 cursor-not-allowed'
                    }`}
                  title="Rotate Ship"
                >
                  <BiRotateRight size={24} />
                  <span className="text-xs">Rotate</span>
                </button>

                <button
                  onClick={() => selectedShip && placedShips.has(selectedShip.id) && handleDeleteShip(selectedShip.id)}
                  disabled={!selectedShip || !placedShips.has(selectedShip?.id) || isSaved}
                  className={`p-2 rounded-xl font-medium flex flex-col items-center justify-center gap-1 transition-all duration-200 aspect-square ${selectedShip && placedShips.has(selectedShip.id) && !isSaved
                    ? 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30'
                    : 'bg-slate-800/50 text-slate-600 cursor-not-allowed'
                    }`}
                  title="Remove Ship"
                >
                  <FaTrash size={20} />
                  <span className="text-xs">Remove</span>
                </button>
              </div>

              <div className="h-px bg-slate-700/50 w-full my-1"></div>

              <button
                onClick={handleAutoPlace}
                disabled={placedShips.size === SHIPS.length || isSaved}
                className={`w-full p-3 rounded-xl font-medium flex items-center justify-center gap-1 transition-all duration-300 ${placedShips.size < SHIPS.length && !isSaved
                  ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                  : 'bg-slate-800/50 text-slate-600 cursor-not-allowed'
                  }`}
              >
                <FaShip size={16} />
                <span>Auto Place</span>
              </button>

              <button
                onClick={handleClearPlacement}
                disabled={placedShips.size === 0 || isSaved}
                className={`w-full p-3 rounded-xl font-medium flex items-center justify-center gap-1 transition-all duration-300 ${placedShips.size > 0 && !isSaved
                  ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                  : 'bg-slate-800/50 text-slate-600 cursor-not-allowed'
                  }`}
              >
                <FaTrash size={16} />
                <span>Clear All</span>
              </button>
            </div>
          </div>

          {/* Center Column - Grid */}
          <div className="flex-1 bg-slate-900/50 rounded-3xl border border-slate-700/50 shadow-xl flex items-center justify-center p-7 backdrop-blur-sm relative overflow-hidden">
            {/* Background decorative elements */}
            <div className="absolute inset-0 opacity-5 pointer-events-none"
              style={{ backgroundImage: 'radial-gradient(circle at center, #94a3b8 1px, transparent 1px)', backgroundSize: '24px 24px' }}>
            </div>

            <div className="relative z-10 p-3 bg-slate-950/80 rounded-2xl border-2 border-slate-700/50 shadow-2xl">
              <div className="grid gap-px bg-slate-800 p-1 rounded-xl border border-slate-700">
                <div className="grid" style={{ gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`, gridTemplateRows: `repeat(${GRID_SIZE}, 1fr)`, gap: '4px', width: '450px', height: '450px' }}>
                  {grid.map((row, y) =>
                    row.map((cell, x) => {
                      const ship = cell ? SHIPS.find(s => s.id === cell) : null;
                      const isSelected = selectedShip?.id === cell;
                      const shipPosition = ship ? placedShips.get(ship.id) : null;
                      const shipRotation = ship ? (shipRotations.get(ship.id) || 0) : 0;

                      // Only make the first cell of the ship draggable
                      const isShipStart = ship && shipPosition &&
                        shipPosition.x === x && shipPosition.y === y;

                      return (
                        <Cell
                          key={`${x}-${y}`}
                          x={x}
                          y={y}
                          onDrop={handleDrop}
                          onHover={handleHover}
                          onClick={() => handleGridCellClick(x, y)}
                          disabled={isSaved}
                          gridSize={GRID_SIZE}
                          isValidPlacement={hoverCoords && checkValidPlacement(
                            hoverCoords.x,
                            hoverCoords.y,
                            hoverCoords.ship,
                            hoverCoords.isVertical
                          )}
                        >
                          {ship && (
                            <PlacedShipCell
                              ship={ship}
                              isSelected={isSelected}
                              isShipStart={isShipStart}
                              shipRotation={shipRotation}
                              disabled={isSaved}
                              onSelect={() => setSelectedShip(ship)}
                            />
                          )}
                        </Cell>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Ready Actions */}
          <div className="w-1/5 bg-slate-900/50 rounded-3xl border border-slate-700/50 shadow-xl p-5 flex flex-col gap-3 backdrop-blur-sm">

            {/* Exit Button */}
            <div className="flex justify-end mb-2">
              <button
                onClick={() => navigate('/')}
                className="p-2 rounded-lg bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
                title="Return to Home"
              >
                <FaArrowLeft size={16} />
              </button>
            </div>

            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest text-center">
              Mission Status
            </h3>

            {/* Status Messages */}
            <div className="flex-1 flex flex-col justify-center">
              {(errorMessage || isSaved || statusMessage) ? (
                <div className="space-y-3">
                  {errorMessage && (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-center text-sm">
                      {errorMessage}
                    </div>
                  )}
                  {isSaved && (
                    <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-xl text-green-400 text-center text-sm">
                      Fleet locked in! <br /> Waiting for battle...
                    </div>
                  )}
                  {statusMessage && (
                    <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl text-blue-400 text-center text-sm">
                      {statusMessage}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-slate-500 text-sm">
                  <p>Deploy all ships to proceed.</p>
                  <p className="mt-2 text-xs opacity-50">Drag ships from the dock below</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="space-y-2 mt-auto">
              {/* Confirm Button */}
              <button
                onClick={handleComplete}
                disabled={placedShips.size !== SHIPS.length || isSaving || isSaved}
                className={`w-full py-3 rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center gap-1 ${placedShips.size === SHIPS.length && !isSaving && !isSaved
                  ? 'bg-green-500 hover:bg-green-400 text-white shadow-lg shadow-green-500/20'
                  : 'bg-slate-800 text-slate-600 cursor-not-allowed'
                  }`}
              >
                {isSaving ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white/50"></div>
                ) : (
                  <>
                    <FaCheck size={18} />
                    Confirm
                  </>
                )}
              </button>

              {/* Ready Button */}
              {isSaved && (
                <button
                  onClick={handleReady}
                  className="w-full py-3 rounded-xl font-bold text-lg transition-all duration-300 bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/20 animate-pulse"
                >
                  BATTLE!
                </button>
              )}
            </div>

            {/* Progress Bar */}
            <div className="pt-4 border-t border-slate-700/50">
              <div className="flex justify-between text-xs text-slate-400 mb-2">
                <span>Deployment</span>
                <span>{placedShips.size}/{SHIPS.length}</span>
              </div>
              <div className="w-full bg-slate-800 rounded-full h-1.5 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${placedShips.size === SHIPS.length ? 'bg-green-500' : 'bg-blue-500'
                    }`}
                  style={{ width: `${(placedShips.size / SHIPS.length) * 100}%` }}
                />
              </div>
            </div>
          </div>

        </div>

        {/* Bottom Dockyard Row */}
        <div className="h-40 bg-slate-900/50 rounded-3xl border border-slate-700/50 shadow-xl backdrop-blur-sm flex flex-col items-center justify-center relative overflow-hidden shrink-0">
          <h3 className="absolute top-3 text-xs font-bold text-slate-500 uppercase tracking-widest">
            Naval Dockyard
          </h3>
          <div className="w-full h-full flex items-center justify-center gap-5 px-7 pt-5 overflow-x-auto">
            {SHIPS.map((ship) => (
              <DockShip
                key={ship.id}
                ship={ship}
                isPlaced={placedShips.has(ship.id)}
                isSelected={selectedShip?.id === ship.id}
                onClick={() => {
                  if (placedShips.has(ship.id)) return;
                  setSelectedShip(selectedShip?.id === ship.id ? null : ship);
                }}
              />
            ))}
          </div>
        </div>

      </div>
    </DndProvider>
  );
};

export default ShipPlacement_Desktop;
